<template>
  <!-- OAuth profile completion section -->
  <v-container
    grid-list-xl
    text-center
    role="region"
    aria-label="Complete OAuth profile"
  >
    <v-layout
      wrap
      class="primary"
      role="presentation"
    >
      <v-flex
        xs12
        sm12
        md12
        lg6
        offset-lg3
      >
        <v-card color="secondary">
          <!-- Heading bar -->
          <v-app-bar
            color="primary"
            flat
          >
            <v-toolbar-title
              class="text-body-2"
              role="heading"
              aria-level="1"
            >
              {{ $t('components.oauthProfile.steppers.setupAccount') }}
            </v-toolbar-title>
          </v-app-bar>

          <v-container
            fluid
            grid-list-md
          >
            <!-- Error alerts -->
            <div
              v-for="(errorsGroup, i) in filteredErrors"
              :key="i"
              role="alert"
              aria-live="assertive"
            >
              <v-alert
                v-for="error in errorsGroup"
                :key="error.id"
                :type="error.type"
                :icon="error.icon"
                dense
              >
                {{ error.msg }}
              </v-alert>
            </div>

            <!-- Optional download-client menu (desktop only) -->
            <v-layout
              justify-center
              align-center
            >
              <v-flex
                text-center
                flex
                xs12
                sm8
                md9
                mb-5
              >
                <v-menu
                  v-if="downloadGames.length && $vuetify.breakpoint.mdAndUp"
                  bottom
                  offset-y
                  color="secondary"
                  role="menu"
                  aria-label="Game client downloads"
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="accent"
                      aria-label="Download game client"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ $t('components.register.buttons.download') }}
                    </v-btn>
                  </template>

                  <v-list role="menu">
                    <v-list-item
                      v-for="game in downloadGames"
                      :key="game.id"
                      role="menuitem"
                      link
                      @click="goTo(game.link)"
                    >
                      <div
                        v-if="game.useImageAsBackground"
                        class="v-list-item-background-image"
                        :style="`background-image: url('${game.img}')`"
                        role="img"
                        :aria-label="$t(game.title)"
                      >
                        <div class="v-list-item-download-labels">
                          <span
                            v-if="game.isNew"
                            class="downloadLabel downloadNew"
                          >New</span>
                          <span
                            v-if="game.isRecommend"
                            class="downloadLabel downloadRecommend"
                          >Recommend</span>
                          <span
                            v-if="game.isRecommend"
                            class="downloadLabel downloadComingSoon"
                          >Coming Soon</span>
                        </div>
                      </div>

                      <template v-else>
                        <v-list-item-avatar size="25">
                          <v-icon>{{ game.icon }}</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-title
                          class="text-no-wrap"
                        >
                          {{ $t(game.title) }}
                          <div class="v-list-item-download-labels">
                            <span
                              v-if="game.isNew"
                              class="downloadLabel downloadNew"
                            >New</span>
                            <span
                              v-if="game.isRecommend"
                              class="downloadLabel downloadRecommend"
                            >Recommend</span>
                            <span
                              v-if="game.isRecommend"
                              class="downloadLabel downloadComingSoon"
                            >Coming Soon</span>
                          </div>
                        </v-list-item-title>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-flex>
            </v-layout>

            <!-- Community & Gift info banners -->
            <v-alert
              :value="discordCommunity.enabled"
              :type="discordCommunity.type"
              :icon="discordCommunity.icon"
              class="alert-discord"
              role="link"
              aria-label="Join our Discord community"
              @click="onClickDiscordLink"
            >
              {{ $t('components.register.communities.discord') }}
            </v-alert>

            <v-alert
              :value="newbieGift.link"
              :type="newbieGift.type"
              :icon="newbieGift.icon"
              class="alert-discord"
              role="link"
              :aria-label="$t(newbieGift.name)"
              @click="onClickGiftsLink"
            >
              {{ $t(newbieGift.name) }}
            </v-alert>

            <!-- Profile completion form -->
            <v-form
              role="form"
              aria-label="Set account credentials"
              @submit.prevent="validate"
            >
              <!-- Username -->
              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    v-model="form.username"
                    v-validate="'required|min:6|max:16'"
                    prepend-icon="fal fa-user"
                    color="accent"
                    data-vv-name="username"
                    :error-messages="errors.collect('username')"
                    :label="$t('components.oauthProfile.form.username') + ' *'"
                    autocomplete="username"
                    aria-required="true"
                  />
                </v-flex>
              </v-layout>

              <!-- Email -->
              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    v-model="form.email"
                    v-validate="'required|email|min:6|max:255'"
                    prepend-icon="fal fa-envelope"
                    color="accent"
                    data-vv-name="email"
                    :error-messages="errors.collect('email')"
                    :label="$t('components.oauthProfile.form.email') + ' *'"
                    autocomplete="email"
                    aria-required="true"
                  />
                </v-flex>
              </v-layout>

              <!-- Password -->
              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    ref="password"
                    v-model="form.password"
                    v-validate="'required|min:6|max:50'"
                    prepend-icon="fal fa-key"
                    color="accent"
                    data-vv-name="password"
                    :error-messages="errors.collect('password')"
                    :label="$t('components.oauthProfile.form.password') + ' *'"
                    type="password"
                    autocomplete="new-password"
                    aria-required="true"
                  />
                </v-flex>
              </v-layout>

              <!-- Confirm password -->
              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    v-model="form.confirmPassword"
                    v-validate="'required|min:6|max:50|confirmed:password'"
                    prepend-icon="fal fa-key"
                    color="accent"
                    data-vv-name="confirmPassword"
                    :error-messages="errors.collect('confirmPassword')"
                    :label="$t('components.oauthProfile.form.confirmPass') + ' *'"
                    type="password"
                    autocomplete="new-password"
                    aria-required="true"
                  />
                </v-flex>
              </v-layout>

              <!-- Terms switch -->
              <v-layout>
                <v-flex xs12>
                  <v-switch
                    v-model="form.acceptedTerms"
                    color="accent"
                    :aria-checked="form.acceptedTerms"
                  >
                    <template #label>
                      <div>
                        {{ $t('components.register.form.acceptTerms') }}
                        <v-tooltip bottom>
                          <template #activator="{ on }">
                            <a
                              target="_blank"
                              :href="toTCLink"
                              class="register-important-link"
                              aria-label="Read Terms and Conditions"
                              @click.stop
                              v-on="on"
                            >
                              Learn More
                            </a>
                          </template>
                          {{ $t('components.register.form.acceptTermsDialog') }}
                        </v-tooltip>
                      </div>
                    </template>
                  </v-switch>
                </v-flex>
              </v-layout>

              <!-- Marketing news switch -->
              <v-layout>
                <v-flex xs12>
                  <v-switch
                    v-model="form.acceptMarketingNews"
                    color="accent"
                    flat
                    :aria-checked="form.acceptMarketingNews"
                  >
                    <template #label>
                      <div>
                        {{ $t('components.register.form.acceptMarketingNews') }}
                      </div>
                    </template>
                  </v-switch>
                </v-flex>
              </v-layout>

              <!-- Policy switch -->
              <v-layout>
                <v-flex xs12>
                  <v-switch
                    v-model="form.acceptedPolicy"
                    color="accent"
                    :aria-checked="form.acceptedPolicy"
                  >
                    <template #label>
                      <div>
                        {{ $t('components.register.form.acceptPolicy') }}
                        <v-tooltip bottom>
                          <template #activator="{ on }">
                            <a
                              target="_blank"
                              :href="toPolicyLink"
                              aria-label="Read Privacy Policy"
                              class="register-important-link"
                              @click.stop
                              v-on="on"
                            >
                              Learn More
                            </a>
                          </template>
                          {{ $t('components.register.form.acceptPolicyDialog') }}
                        </v-tooltip>
                      </div>
                    </template>
                  </v-switch>
                </v-flex>
              </v-layout>

              <!-- Loading bar -->
              <v-progress-linear
                v-show="isLoading"
                color="accent"
                indeterminate
                role="status"
                aria-label="Submitting form"
              />

              <!-- Submit -->
              <v-btn
                color="accent"
                type="submit"
                :disabled="!(form.acceptedPolicy && form.acceptedTerms)"
                :aria-label="$t('components.oauthProfile.buttons.change')"
              >
                {{ $t('components.oauthProfile.buttons.change') }}
              </v-btn>
            </v-form>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import actions from '@/store/actions';
import { filterByKeys } from "@/utils/object.utils";
import Validator from "@/modules/validator.module";
import blacklist from "@/constants/blacklist";
import downloadGames from "@/constants/download.games";
import communities from "@/constants/communities.js";
import links from "@/constants/links";

const ValidatorModule = new Validator();

export default {
  name: "SetOauthProfileForm",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    ValidationProvider
  },
  inject: ["$validator"],
  emits: {'complete-oauth': null},
  data() {
    const defaultForm = Object.freeze({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        acceptedPolicy: false,
        acceptedTerms: false,
        acceptMarketingNews: false,
    });

    return {
        form: Object.assign({}, defaultForm),
        downloadGames: downloadGames.list,
        newbieGift: {
            ...links.newbieGift,
            link: links.general.registerGiftLink
        },
        oAuthLinks: links.oAuth,
        isLoading: false,
        defaultForm
    }
  },
  computed: {
    filteredErrors() {
      return filterByKeys(
        this.$validator.errors.collect(),
        blacklist.forms.completeOAuthProfile
      );
    },
    toTCLink() {
        return links.routes.tcConditions;
    },
    toPolicyLink() {
        return links.routes.tcPolicy;
    },
    discordCommunity() {
        return {
            enabled: true,
            type: "info",
            ...(communities.list.find((community) => community.id === 2) || {}),
        }
    },
  },
  watch: {
    'form.acceptedPolicy'(newValue) {
        if (newValue) {
            ValidatorModule.clear();

            return;
        }

        ValidatorModule.add("acceptedPolicy", {
            type: 0,
            msg: 'Policy is required to be accepted to proceed'
        });
    },
    'form.acceptedTerms'(newValue) {
        if (newValue) {
            ValidatorModule.clear();

            return;
        }

        ValidatorModule.add("acceptedTerms", {
            type: 0,
            msg: 'Terms & Conditions is required to be accepted to proceed'
        });
    }
  },
  methods: {
    async validate() {
      this.isLoading = true;

        if (await this.$validator.validateAll()) {
            if (!this.form.acceptedTerms ||
            !this.form.acceptedPolicy) {
                return;
            }

          this.$api.completeOAuthProfile(
            this.form.username,
            this.form.email,
            this.form.password,
            this.form.acceptMarketingNews
          )
            .then(res => {
              this.isLoading = false;
              // dispatch the user new data
              actions.updateUser(res.user);
              // emitted completed oauth
              this.$emit('complete-oauth');
              // Add to the validator
              ValidatorModule.add("completedOAuthProfile", res);
            })
            .catch(() => {
              this.isLoading = false;
            });
        }else{
            this.isLoading = false;
        }
    },
    onClickDiscordLink() {
        // go to link
        window.open(this.discordCommunity.link);
    },
    onClickGiftsLink() {
        // go to link
        window.open(this.newbieGift.link);
    },
  }
};
</script>
